import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import {
  RemittanceDocSchema,
  deductionSchema,
  invoiceReferenceSchema,
  remittanceProvenanceSchema,
} from "./remittance-schemas";

/**
 * Create Pending Payment Step
 *
 * Creates an AR payment record (status: PENDING) from a normalized remittance
 * document. The payment is stored for later matching against Plaid bank
 * transactions.
 *
 * Note: This is a backend-only step that requires database access.
 * The handler is registered in backend/src/workflows/steps/handlers/
 */

export const PAYMENT_METHODS = [
  "check",
  "wire",
  "ach",
  "eft",
  "credit_card",
  "other",
] as const;

export const createPendingPaymentStep = defineStepConfig({
  type: "create_pending_payment",
  name: "Create Pending Payment",
  description:
    "Record a remittance advice from a normalized remittance document, set its customer, and link it to its cash receipt when one is already here",
  category: "Data Access",
  libraryModule: "AccountsReceivable",
  inputSchema: z.object({
    status: z
      .enum(["success", "extraction_failed"])
      .describe("Normalization status from the upstream step"),
    failureReason: z.string().optional(),
    extractionResultId: z.uuid().optional(),
    remittanceDoc: RemittanceDocSchema.optional().describe(
      "Normalized remittance fields from the upstream normalize_remittance_document step. Present when status is success.",
    ),
    provenance: remittanceProvenanceSchema
      .optional()
      .describe(
        "Where the remittance came from (channel, raw reference, received time, sender, source ids). The advice is recorded from this.",
      ),
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL)
      .optional()
      .describe(
        "Legacy input for workflow versions wired before `provenance`: email provenance is built from it when `provenance` is absent.",
      ),
    document: entityOfType(SystemEntity.CORE_DOCUMENT)
      .optional()
      .describe(
        "Source document — populates remittance_advice.document_id and disambiguates dedup keys for emails with multiple attachments",
      ),
    customer: entityOfType(SystemEntity.AR_CUSTOMER)
      .nullable()
      .optional()
      .describe(
        "The customer the payment belongs to, identified upstream by identify_customer. When null or absent (or our own organization), no payment is created before cash: the advice matches its waiting cash receipt if it can, else persists with payment_id IS NULL and the step returns customer_unmatched.",
      ),
  }),

  outputSchema: z.object({
    status: z
      .enum([
        "success",
        "extraction_failed",
        "customer_unmatched",
        "receipt_conflict",
      ])
      .describe(
        "'success' when the remittance's customer is known (identified, the source's hint, or taken from the cash receipt it matched); 'extraction_failed' passthrough; 'customer_unmatched' when no customer was identified and no matched receipt supplied one; 'receipt_conflict' when the cash receipt the source named could not take this remittance (it is linked to a different receipt, or the named receipt is already settled by others) — the advice persists for AR triage",
      ),
    failureReason: z.string().optional(),
    payment: entityOfType(SystemEntity.AR_PAYMENT)
      .optional()
      .describe("Never set since GEN-3202: no AR Payment is created"),
    paymentId: z
      .uuid()
      .optional()
      .describe("Never set since GEN-3202: no AR Payment is created"),
    bankTransactionId: z
      .uuid()
      .optional()
      .describe(
        "The cash receipt the remittance is linked to, when the source named it or the intake match found it",
      ),
    remittanceAdviceId: z
      .uuid()
      .optional()
      .describe(
        "ar_remittance_advice id for the recorded intake. Present whenever the advice was persisted (success, customer_unmatched and receipt_conflict).",
      ),
    customerSource: z
      .enum([
        "identify_customer",
        "provenance_hint",
        "matched_receipt",
        "saved_remittance",
        "none",
      ])
      .optional()
      .describe(
        "Where the remittance's customer came from: identify_customer, the source's provenance.customerId hint (used only when identification found none), matched_receipt (no customer was identified, but the remittance matched a waiting cash receipt and took its customer), saved_remittance (neither, but the remittance already had a customer from an earlier reading or a person), or none.",
      ),
    invoiceReferences: z.array(invoiceReferenceSchema),
    deductions: z.array(deductionSchema).optional(),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
